import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CounselModal } from '../components/CounselModal';
import { RoomBackdrop } from '../components/RoomBackdrop';
import {
  NEED_IDS,
  OFFERINGS,
  counselOfferBySlug,
  counselOffersForNeed,
} from '../data/offerings';
import './HomePage.css';
import './ServicesPage.css';

const DEFAULT_NEED = 'lead';

export function ServicesPage() {
  const [params, setParams] = useSearchParams();
  const needParam = params.get('need');
  const active = NEED_IDS.includes(needParam) ? needParam : DEFAULT_NEED;
  const openSlug = params.get('offer');

  useEffect(() => {
    if (NEED_IDS.includes(needParam)) return;
    const next = new URLSearchParams(params);
    next.set('need', DEFAULT_NEED);
    setParams(next, { replace: true });
  }, [needParam, params, setParams]);

  const cards = useMemo(() => counselOffersForNeed(active), [active]);
  const openOffer = openSlug ? counselOfferBySlug(openSlug) : undefined;
  const activePillar = OFFERINGS[active];

  const setOpen = (offer) => {
    const next = new URLSearchParams(params);
    next.set('need', active);
    if (offer) next.set('offer', offer.slug);
    else next.delete('offer');
    setParams(next, { replace: true });
  };

  const setNeed = (id) => {
    const next = new URLSearchParams();
    next.set('need', id);
    setParams(next);
  };

  return (
    <>
      <RoomBackdrop room="services" dim={0} fit="contain" />
      <main className="page services">
        <header className="services__header">
          <motion.h1
            className="services__title"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            Services
          </motion.h1>
          <div className="services__filters" role="tablist" aria-label="Filter by need">
            {NEED_IDS.map((id) => {
              const isOn = active === id;
              return (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={isOn}
                  className={isOn ? 'services__filter services__filter--on' : 'services__filter'}
                  onClick={() => setNeed(id)}
                >
                  {OFFERINGS[id].title}
                </button>
              );
            })}
          </div>
          {activePillar ? (
            <p className="services__lede">{activePillar.tagline}</p>
          ) : null}
        </header>

        <div className="services__grid" data-count={cards.length}>
          {cards.map((card, i) => (
            <motion.button
              key={card.slug}
              type="button"
              className="path-card services__card"
              onClick={() => setOpen(card)}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 * i, ease: [0.22, 1, 0.36, 1] }}
            >
              <h2 className="path-card__title">{card.title}</h2>
              <p className="path-card__line">{card.blurb}</p>
              <span className="path-card__more">Details →</span>
            </motion.button>
          ))}
        </div>
      </main>

      {openOffer ? <CounselModal offer={openOffer} onClose={() => setOpen(null)} /> : null}
    </>
  );
}
